/**
 * Manages error table state: tableData array, modifiedData for tracking
 * edits, and regeneration of column data when errors change.
 */

import { useEffect, useState, useCallback } from 'react';
import type { CellChangeData } from '../components/EditableCell';
import { generatePropertyRow } from '../components/PropertyErrorRow';
import { generateOrderRow } from '../components/OrderErrorRow';
import type { ErrorCell } from '../../fix-errors/utils/fixErrorFieldMaps';
import { ORDER_OPTIONAL_FIELDS } from '../../fix-errors/utils/fixErrorFieldMaps';

// ---- Types ----------------------------------------------------------------

interface ErrorTableRow {
  id: number;
  [field: string]: ErrorCell | number | string | undefined;
}

interface UseErrorTableDataParams {
  ErrorTableData: ErrorTableRow[];
  isProperty: boolean;
  fixErrorTableData: string | null;
  fixErrorTableDataForOrder: ErrorTableRow[] | undefined;
  fileChangeAfterFix: boolean;
  isClearData: boolean;
  setResult: (data: ErrorTableRow[] | null) => void;
  setDisabled: (disabled: boolean) => void;
  setFixErrorTableData: (data: string) => void;
  setFileChangeAfterFix: (v: boolean) => void;
  setIsClearData: (v: boolean) => void;
}

interface UseErrorTableDataReturn {
  tableData: Record<string, { component: React.ReactNode; data?: string | null }>[];
  isLoading: boolean;
  setData: (change: CellChangeData) => void;
}

// ---- Helper: format camelCase to spaced string ----------------------------

const formatFieldName = (input: string): string =>
  input.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

// ---- Access type field dependency rules -----------------------------------

const applyAccessTypeDependencies = (
  row: ErrorTableRow,
  fieldName: string
): void => {
  if (fieldName !== 'accessType') return;

  const accessValue = (row.accessType as ErrorCell)?.value;

  if (accessValue === 'Keypad') {
    (row.lockboxLocation as ErrorCell).isActive = false;
    (row.rentlyRegistrationPin as ErrorCell).isActive = false;
  }
  if (accessValue === 'Lockbox') {
    (row.rentlyRegistrationPin as ErrorCell).isActive = false;
  }
  if (accessValue === 'Rently') {
    (row.lockboxLocation as ErrorCell).isActive = false;
    (row.accessCode as ErrorCell).isActive = false;
  }
  if (
    accessValue === 'SmartRent' ||
    accessValue === 'ShowMojo' ||
    accessValue === 'Tenant Turner'
  ) {
    (row.lockboxLocation as ErrorCell).isActive = false;
    (row.accessCode as ErrorCell).isActive = false;
    (row.rentlyRegistrationPin as ErrorCell).isActive = false;
  }
};

// ---- Hook -----------------------------------------------------------------

export const useErrorTableData = ({
  ErrorTableData,
  isProperty,
  fixErrorTableData,
  fixErrorTableDataForOrder,
  fileChangeAfterFix,
  isClearData,
  setResult,
  setDisabled,
  setFixErrorTableData,
  setFileChangeAfterFix,
  setIsClearData,
}: UseErrorTableDataParams): UseErrorTableDataReturn => {
  const [tableData, setTableData] = useState<
    Record<string, { component: React.ReactNode; data?: string | null }>[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modifiedData, setModifiedData] = useState<ErrorTableRow[] | null>(
    null
  );
  const [pendingChange, setPendingChange] = useState<CellChangeData | undefined>();

  // ---- Generate rows from data source ------------------------------------

  const regenerateRows = useCallback(
    (source: ErrorTableRow[]): Record<string, { component: React.ReactNode; data?: string | null }>[] => {
      if (isProperty) {
        return source.map((row) =>
          generatePropertyRow(row as unknown as Parameters<typeof generatePropertyRow>[0], (c) => setPendingChange(c))
        );
      }
      return source.map((row) =>
        generateOrderRow(row as unknown as Parameters<typeof generateOrderRow>[0], (c) => setPendingChange(c))
      );
    },
    [isProperty]
  );

  // ---- Clear data when requested ------------------------------------------

  useEffect(() => {
    if (isClearData) {
      setModifiedData(null);
      setIsClearData(false);
    }
  }, [isClearData]);

  // ---- Generate initial column data when ErrorTableData changes -----------

  useEffect(() => {
    setIsLoading(true);
    const rows = regenerateRows(ErrorTableData);
    setTableData(rows);
    setIsLoading(false);
  }, [ErrorTableData, regenerateRows]);

  // ---- Re-generate after partial fix (fileChangeAfterFix) -----------------

  useEffect(() => {
    if (!fileChangeAfterFix) return;

    if (isProperty) {
      setTableData(regenerateRows(ErrorTableData));
    } else if (fixErrorTableDataForOrder) {
      setTableData(regenerateRows(fixErrorTableDataForOrder));
    }
    setFileChangeAfterFix(false);
  }, [fileChangeAfterFix, fixErrorTableDataForOrder, ErrorTableData]);

  // ---- Apply cell edits when pendingChange fires --------------------------

  useEffect(() => {
    if (!pendingChange) return;

    const data = pendingChange;

    // Determine working copy
    let working: ErrorTableRow[];
    if (fixErrorTableDataForOrder && !isProperty) {
      working = fixErrorTableDataForOrder;
    } else {
      working = modifiedData ?? ErrorTableData;
    }

    // Apply change
    working.forEach((row) => {
      if (row.id !== data.id) return;

      const cell = row[data.name] as ErrorCell | undefined;
      if (!cell) return;

      cell.isActive = data.isActive;
      cell.value = data.value ?? null;
      cell.errorMessage = data.errorMessage;

      if (cell.isActive !== false) {
        cell.isActive = data.isActive;
      }
      if (cell.isActive && !cell.errorMessage) {
        cell.errorMessage = `Not a valid ${formatFieldName(data.name)}`;
      }
      if (data.name === 'unitNumber') {
        cell.isActive = false;
      }

      // Access type field dependencies (order flow only)
      if (!isProperty) {
        applyAccessTypeDependencies(row, data.name);
      }
    });

    setModifiedData(working);

    if (!isProperty) {
      setFixErrorTableData(JSON.stringify(working));
    }

    // Check if data was modified from original
    if (JSON.stringify(working) === JSON.stringify(ErrorTableData)) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }

    // Regenerate display rows
    if (!isProperty && working && !fileChangeAfterFix) {
      setTableData(regenerateRows(working));
    } else if (isProperty && working) {
      setTableData(regenerateRows(working));
    }

    setResult(working);
    setPendingChange(undefined);
  }, [pendingChange]);

  return {
    tableData,
    isLoading,
    setData: (change: CellChangeData) => setPendingChange(change),
  };
};
